import { useSearch } from "@tanstack/react-router";
import { Button } from "antd";
import { useDestroyOfferCustomField } from "api/TenantSlug/OfferCustomFields/hooks.ts";
import { AddCustomRuleFieldForm } from "components/Forms/AddCustomRuleFieldForm/AddCustomRuleFieldForm.tsx";
import { DeleteModal } from "components/other/DeleteModal/DeleteModal.tsx";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useState } from "react";
import { toast } from "react-toastify";
import { DrawerId, FormId } from "types/models.ts";

interface Props {
    offerId: string;
    readOnly?: boolean;
    onSubmit?: () => void;
}

export const AddCustomRuleFieldDrawer: FC<Props> = ({ offerId, readOnly, onSubmit }) => {
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const drawerUtils = useDrawer();
    const { t } = usePaldockTranslation("drawers.add-custom-rule-field");
    const { t: tC } = usePaldockTranslation("common");
    const search = useSearch({ strict: false }) as { customFieldId?: string };
    const customFieldId = search.customFieldId;

    const { mutate: destroyField, isPending } = useDestroyOfferCustomField(offerId, customFieldId ?? "", {});

    const handleClose = () => {
        drawerUtils.hide(DrawerId.AddCustomRuleField, ["customFieldId"]);
    };

    const handleSubmit = () => {
        onSubmit?.();
        handleClose();
    };

    const onDelete = () => {
        destroyField(void null, {
            onSuccess: () => {
                setDeleteModalOpen(false);
                toast.success(t("messages.delete_success"));
                handleClose();
                onSubmit?.();
            },
            onError: () => {
                toast.error(t("messages.delete_error"));
            },
        });
    };

    return (
        <>
            <Drawer
                drawerId={DrawerId.AddCustomRuleField}
                title={<DrawerTitle mainTitle={t("title")} />}
                searchParamsToClean={["customFieldId"]}
                onBeforeClose={() => {
                    setDeleteModalOpen(false);
                    return true;
                }}
                extraButtons={
                    <>
                        {customFieldId && !readOnly && (
                            <Button
                                size={"large"}
                                danger
                                type={"primary"}
                                disabled={isPending}
                                onClick={() => setDeleteModalOpen(true)}
                            >
                                {tC("delete")}
                            </Button>
                        )}
                        {!readOnly && (
                            <Button
                                size={"large"}
                                htmlType="submit"
                                type={"primary"}
                                form={FormId.AddCustomRuleFieldForm}
                            >
                                {tC("save")}
                            </Button>
                        )}
                    </>
                }
                destroyOnClose={true}>
                <DrawerContent>
                    <DrawerCollapsible title={t('section_title')}>
                        <AddCustomRuleFieldForm offerId={offerId} customFieldId={customFieldId} readOnly={readOnly} onSubmit={handleSubmit} />
                    </DrawerCollapsible>
                </DrawerContent>
            </Drawer>
            <DeleteModal
                open={deleteModalOpen}
                title={t("delete_modal_title")}
                onCancel={() => setDeleteModalOpen(false)}
                onConfirm={onDelete}
                loading={isPending}
            />
        </>
    )
}
